/**
 * 说明书上传附件权限策略
 * @module specification-upload-attachment-strategy
 */

import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type {
    FeaturePermissionStrategy,
    FeaturePermissionContext,
    PermissionCheckResult,
    FeatureKey,
} from '@telecom/shared/domain/permission';

/**
 * 说明书上传附件权限策略
 *
 * 权限规则：
 * - 免费版：需要检查用量配额，有用量时可使用，无用量时提示升级
 * - Pro版及以上：不限制使用
 */
@injectable()
export class SpecificationUploadAttachmentStrategy implements FeaturePermissionStrategy {
    readonly featureId: FeatureKey = '说明书:上传附件';

    async checkPermission(context: FeaturePermissionContext): Promise<PermissionCheckResult> {
        const { featureId, permissionService } = context;
        const planType = permissionService.getUserPlan()?.planType;

        // 免费版用户需要检查配额
        if (planType === 'free') {
            // 检查是否有用量配额
            const hasQuota = permissionService.hasQuota(featureId);

            if (!hasQuota) {
                // 没有配额时提示升级
                return {
                    granted: false,
                    reason: 'quota_exceeded',
                    suggestedAction: 'upgrade_to_pro',
                    message: '升级 pro',
                };
            }

            // 有配额则允许使用
            return {
                granted: true,
            };
        }

        // Pro版及以上用户不限制
        // 包括 'pro', 'enterprise' 或其他付费套餐
        return {
            granted: true,
        };
    }

    async recordUsage(context: FeaturePermissionContext): Promise<void> {
        const { featureId, permissionService } = context;
        const planType = permissionService.getUserPlan()?.planType;

        // 只有免费版用户需要记录使用量
        if (planType === 'free') {
            console.log('[说明书上传附件] 免费版用户使用量已记录', featureId);
            // TODO: 实际的使用量记录逻辑
            // 可能需要调用 API 扣减配额
        } else {
            console.log(`[说明书上传附件] ${planType} 用户无需记录使用量`);
        }
    }
}
